import { CloseCircle } from '@crehana/compass.icons.outline/v2/close-circle';
import { Eye } from '@crehana/compass.icons.outline/v2/eye';
import { AlertDialog } from '@crehana/compass.ui/v3/dialog';
import { toast } from '@crehana/compass.ui/v3/toaster';
import { BasicTooltip } from '@crehana/compass.ui/v3/tooltip';
import { format } from '@crehana/date/format';
import { parseISO } from 'date-fns';
import { useState } from 'react';

import { TimeOvertimeRequestStatus } from '@/generated/graphql/types';

import { useCancelOvertimeRequest } from '../../../hooks/use-cancel-overtime-request';
import { useTranslation } from '../../../i18n';
import type { TMyOvertimeRequest } from '../../../types';

type TRequestRowActionsProps = {
  request: TMyOvertimeRequest;
  onCancelled: () => void;
};

export const RequestRowActions = ({
  request,
  onCancelled,
}: TRequestRowActionsProps) => {
  const { t, languageCode } = useTranslation();
  const { cancelRequest, cancelling } = useCancelOvertimeRequest();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const isApproved = request.status === TimeOvertimeRequestStatus.Approved;
  const isPending = request.status === TimeOvertimeRequestStatus.Pending;

  const runCancel = async () => {
    try {
      const ok = await cancelRequest(request.id);
      if (ok) {
        toast.success(t('TOAST_DELETE_SUCCESS'));
        onCancelled();
      } else {
        toast.error(t('TOAST_ERROR_GENERIC'));
      }
    } catch {
      toast.error(t('TOAST_ERROR_GENERIC'));
    }
  };

  const handleCancelClick = () => {
    if (isPending) {
      setConfirmOpen(true);
      return;
    }
    void runCancel();
  };

  const handleConfirmCancel = async () => {
    await runCancel();
    setConfirmOpen(false);
  };

  const formattedDate = format({
    date: parseISO(request.request_date),
    pattern: "d 'de' MMMM",
    languageCode,
  });

  return (
    <div className="tw-flex tw-items-center tw-cds-gap-4">
      <BasicTooltip content={t('REQUESTS_ACTION_VIEW_DETAIL')}>
        <button
          type="button"
          aria-label={t('REQUESTS_ACTION_VIEW_DETAIL')}
          // TIM-559 wires the detail drawer. This handler stays a placeholder on purpose.
          onClick={() => undefined}
          className="tw-inline-flex tw-cursor-pointer tw-cds-text-secondary tw-transition-colors hover:tw-cds-text-brand"
        >
          <Eye size={20} />
        </button>
      </BasicTooltip>

      <BasicTooltip
        content={
          isApproved ? t('ACTION_CANCEL_DISABLED_TOOLTIP') : t('ACTION_CANCEL')
        }
      >
        {isApproved ? (
          <span
            data-testid="request-row-actions-cancel"
            aria-disabled="true"
            aria-label={t('ACTION_CANCEL_DISABLED_TOOLTIP')}
            className="tw-inline-flex tw-cursor-not-allowed tw-cds-text-disabled"
          >
            <CloseCircle size={20} />
          </span>
        ) : (
          <button
            type="button"
            data-testid="request-row-actions-cancel"
            aria-label={t('ACTION_CANCEL')}
            onClick={handleCancelClick}
            className="tw-inline-flex tw-cursor-pointer tw-cds-text-primary tw-transition-colors hover:tw-cds-text-interactive-hover"
          >
            <CloseCircle size={20} />
          </button>
        )}
      </BasicTooltip>

      <AlertDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        icon="alert"
        title={t('DELETE_ALERT_TITLE')}
        description={t('DELETE_ALERT_DESCRIPTION', {
          interpolation: { date: formattedDate },
        })}
        confirmAction={{
          label: t('DELETE_ALERT_CONFIRM'),
          variant: 'alert',
          loading: cancelling,
          onClick: handleConfirmCancel,
        }}
        cancelAction={{
          label: t('DELETE_ALERT_CANCEL'),
        }}
      />
    </div>
  );
};
